// @flow
import type {Node} from 'react';
import type {ViewProps} from 'bfu-auth-client/src/login/types/Types.js';

import React from 'react';
import {useEffect, useState} from 'react';

import BlueflagAuth from 'bfu-auth-client/src/login/data/BlueflagAuth.js';
import Message from 'react-goose/lib/Message';


// users should be redirected here from auth0 successful sign ins
// for example an azure sign in via cognito pool client
// note that whatever route renders this view must be on the list of permitted callback urls 

const AuthFlowLandingView = (props: ViewProps): Node => {
    let {next} = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // On initial page load, attempt to sign in
        setLoading(true);
        BlueflagAuth()
            .signInAuthCode()
            .then(() => next('SIGNIN_SUCCESS'))
            .catch((err) => {
                setError(err);
            })
            .finally(()=>setLoading(false));
    }, [next]);


    return <div style={{
        minHeight: "100vh",
        display: 'flex',
        alignItems: "center", 
        justifyContent: "center"}}>
        {loading && <p>Authenticating, please wait...</p>}
        {error && <AuthErrorView error={error} {...props} />}
    </div>;
};

const AuthErrorView = (props: ViewProps): Node => {
    const {error} = props;
    if(error.name === 'USER_DOES_NOT_EXIST'){
        return <AccountProvisioningMessage {...props}/>;
    }

    return <div style={{
        minHeight: "100vh",
        display: 'flex',
        alignItems: "center", 
        justifyContent: "center"}}>
        <Message style={{maxWidth: "500px"}} spruceName="BlueflagAuthFormMessage" modifier="negative">
            <span>
                The server was unable to authenticate the auth0 code. Please contact administrator if the problem persists.
            </span>
            <br/>
            <br/>
            <span>
                Details: {error.statusCode && error.statusCode >= 400 && error.statusCode < 500 ? error.name : 'No details.'}.
            </span>
            <br/>
            <br/>
            <a href="/" style={{textDecoration: 'none', color: '#007bff'}}>
                Retry Now
            </a>
        </Message>
    </div>;
};

function AccountProvisioningMessage({config}: ViewProps) {
    const [countdown, setCountdown] = useState(60); // Start countdown from 60 seconds

    // If using React Router, you'd use:
    // const navigate = useNavigate();

    // useEffect for the countdown logic
    useEffect(() => {
        // If countdown reaches 0, no need to set further timeouts.
        // The redirect will be handled by the second useEffect.
        if (countdown <= 0) {
            return;
        }

        // Set a timer to decrease the countdown every second
        const timerId = setInterval(() => {
            setCountdown(prevCountdown => prevCountdown - 1);
        }, 1000);

        // Cleanup function: This will clear the interval if the component
        // unmounts before the countdown finishes.
        return () => clearInterval(timerId);
    }, [countdown]); 
    useEffect(() => {
        if (countdown === 0) {
            window.location.href = '/';
        }
    }, [countdown]); // Rerun this effect if `countdown` changes (specifically to check for 0)
                    
    return (
        <div>
            <div style={{width: "300px"}}>
                {config.app.logo &&
                    <img
                        className="BlueflagAuthFormLogo"
                        src={config.app.logo}
                        title={config.app.name}
                        alt={config.app.name}
                    />
                }
            </div>
            <p style={{fontWeight: 'bold', fontSize: '1.1em', marginBottom: '10px'}}>
                Great to have you!
            </p>
            <p style={{marginBottom: '10px'}}>
                Your account is being created right now, and we're also lining up your initial training resources.
            </p>
            <p style={{marginBottom: '10px'}}>
                We're excited for you to get learning! This process is usually quick.
            </p>
            <p style={{fontStyle: 'italic', marginBottom: '15px'}}>
                Please check back in a moment or two to begin your journey.
            </p>
            {countdown > 0 && (
                <p style={{fontSize: '0.9em', color: '#333', marginTop: '15px'}}>
                You will be automatically redirected in {countdown} second{countdown === 1 ? '' : 's'}...
                </p>
            )}
            {countdown === 0 && (
                <p style={{fontSize: '0.9em', color: '#333', marginTop: '15px'}}>
                Redirecting now...
                </p>
            )}
            <br/>
        </div>
    );
}


export default AuthFlowLandingView;